<template>
  <div class="tabs-container" :class="sectionClass">
    <div v-if="data.length < 2" class="empty-tabs">
      <template v-if="data.length === 1 && isPageVisible(data[0])">
        <div class="page-actions-top">
          <FormActionRow level="page" :page-id="data[0].id" :position="['top']" />
        </div>
        <ElementStyleWrapper
          v-if="pageStyleWrapper(data[0]).hasWrapper"
          :css-text="pageStyleWrapper(data[0]).cssText"
          :before-html="pageStyleWrapper(data[0]).beforeHtml"
          :after-html="pageStyleWrapper(data[0]).afterHtml"
        >
          <PanelsBlock
            v-if="panelsReady(data[0])"
            :rows="data[0].panelRows"
            :lazy="data[0].lazy"
            :page-id="data[0].id"
          />
        </ElementStyleWrapper>
        <PanelsBlock
          v-else-if="panelsReady(data[0])"
          :rows="data[0].panelRows"
          :lazy="data[0].lazy"
          :page-id="data[0].id"
        />
      </template>
    </div>
    <div v-else class="tabs">
      <div v-if="visibleTabs.length === 0" class="tab-empty-hidden" aria-hidden="true" />
      <template v-else>
        <div class="tab-headers">
          <button
            v-for="(tab, idx) in visibleTabs"
            :key="String(tab.id) + '-' + idx"
            class="tab-header"
            :class="{ active: currentTab === idx, errors: hasError(tab) }"
            type="button"
            @click="selectTab(tab, idx)"
          >
            <img
              v-if="tabIconImageUrl(tab)"
              class="tab-header-icon"
              :src="tabIconImageUrl(tab)"
              alt=""
            />
            <i
              v-else-if="tabIconPrimeClass(tab)"
              class="tab-header-icon-prime"
              :class="tabIconPrimeClass(tab)"
            />
            {{ tab.name }}
          </button>
        </div>
        <div class="tab-content">
          <div
            v-if="!tabBodyRevealed"
            class="tab-panel-pending"
            aria-busy="true"
            aria-live="polite"
          >
            <span class="tab-panel-pending__spinner" />
          </div>
          <div
            v-if="activeVisibleTab"
            :key="activeTabKey"
            class="tab-panel"
            :class="{ 'tab-panel--pending': !tabBodyRevealed }"
          >
            <div class="page-actions-top">
              <FormActionRow level="page" :page-id="activeVisibleTab.id" :position="['top']" />
            </div>
            <div class="container-fluid">
              <ElementStyleWrapper
                v-if="pageStyleWrapper(activeVisibleTab).hasWrapper"
                :css-text="pageStyleWrapper(activeVisibleTab).cssText"
                :before-html="pageStyleWrapper(activeVisibleTab).beforeHtml"
                :after-html="pageStyleWrapper(activeVisibleTab).afterHtml"
              >
                <PanelsBlock
                  v-if="panelsReady(activeVisibleTab)"
                  :key="activeTabKey"
                  :rows="activeVisibleTab.panelRows"
                  :lazy="activeVisibleTab.lazy"
                  :page-id="activeVisibleTab.id"
                />
              </ElementStyleWrapper>
              <PanelsBlock
                v-else-if="panelsReady(activeVisibleTab)"
                :key="activeTabKey"
                :rows="activeVisibleTab.panelRows"
                :lazy="activeVisibleTab.lazy"
                :page-id="activeVisibleTab.id"
              />
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { normalizePageTabId } from '@/common/composables/useFormStates'
import { resolveElementRegistryFormId } from '@/common/utils/resolveElementRegistryFormId'
import { mergePageDataFieldSnapshot } from '@/common/utils/resolveClientDataActualFields'
import { usePageData } from '@/common/composables/usePageData'
import { usePageErrors } from '@/common/composables/usePageError'
import { parseElementStyleWrapper } from '@/common/utils/elementStyleWrapper'
import {
  buildPageOnClickScriptData,
  isPageTabVisible,
} from '@/common/utils/pageTabMeta'
import { resolvePlatformIcon } from '@/common/utils/resolvePlatformIcon'
import { FormProviderKey, FormScrollOwnerKey } from '@/components/platform/injection-keys'
import ElementStyleWrapper from '@/components/platform/elements/ElementStyleWrapper.vue'
import PanelsBlock from '@/components/platform/panel/Block.vue'
import FormActionRow from './FormActionRow.vue'
import { computed, inject, nextTick, ref, watch } from 'vue'

interface Props {
  data: any[]
  sectionRole?: 'header' | 'footer'
}

const props = defineProps<Props>()

const scrollOwner = inject(FormScrollOwnerKey, computed(() => 'page' as const))

const sectionClass = computed(() => {
  const owner = scrollOwner.value
  const isMain = !props.sectionRole
  const classes: Record<string, boolean> = {
    'form-section--header': props.sectionRole === 'header',
    'form-section--footer': props.sectionRole === 'footer',
    'tabs-container--page-scroll': isMain && owner === 'page',
    'tabs-container--clip-inner': isMain && (owner === 'form' || owner === 'main-body'),
  }
  return classes
})

const currentTab = ref(0)
const lastFocusedTabOnClickSig = ref('')
const tabBodyRevealed = ref(true)
const { pageErrors } = usePageErrors()
const { pageData, pageDataSlot, pageRegistryFormId, pageStates } = usePageData()
const formProvider = inject(FormProviderKey, null)

/** Ленивые страницы: монтируем панели только после первого показа вкладки. */
const lazyPagesMounted = ref<Record<string, boolean>>({})

const pagesDataSlotForVisibility = computed(() => {
  return String(pageDataSlot.value ?? '').trim()
})

const formId = computed(() => {
  if (pageRegistryFormId.value) return pageRegistryFormId.value
  const d = pageData.value as any
  const resolved = resolveElementRegistryFormId(d, pagesDataSlotForVisibility.value)
  if (resolved) return resolved
  if (d?.$fid != null && String(d.$fid) !== '') return String(d.$fid)
  return ''
})

const pageStateVisibility = computed(() => {
  const map: Record<string, boolean> = {}
  for (const state of pageStates.value ?? []) {
    if (String(state?.element_level ?? '').toUpperCase() !== 'PAGE') continue
    if (state?.is_visible === undefined) continue
    const visible = state.is_visible !== false
    if (state.element_id != null) {
      map[normalizePageTabId(state.element_id)] = visible
    }
    const pageName =
      state.element_name != null ? String(state.element_name).trim() : ''
    if (pageName) map[pageName] = visible
  }
  return map
})

const isPageVisible = (tab: any) => isPageTabVisible(tab, pageStateVisibility.value)

const pageStyleWrapper = (tab: any) => parseElementStyleWrapper(String(tab?.style ?? '').trim())

const tabIconResolution = (tab: any) => {
  const image = String(tab?.image ?? '').trim()
  if (!image) return null
  return resolvePlatformIcon(image)
}

const tabIconImageUrl = (tab: any) => {
  const resolution = tabIconResolution(tab)
  return resolution?.kind === 'image' ? resolution.url : ''
}

const tabIconPrimeClass = (tab: any) => {
  const resolution = tabIconResolution(tab)
  return resolution?.kind === 'prime' ? resolution.primeClass : ''
}

const visibleTabs = computed(() => {
  if (!props.data?.length) return []
  if (Object.keys(pageStateVisibility.value).length === 0) return [...props.data]
  return props.data.filter((tab: any) => isPageVisible(tab))
})

const visibleTabIdsKey = computed(() => {
  return visibleTabs.value
    .map((tab: any) => normalizePageTabId(tab?.id ?? tab?._attributes?.id))
    .join('|')
})

const activeVisibleTab = computed(() => {
  const tabs = visibleTabs.value
  if (!tabs.length) return null
  return tabs[currentTab.value] ?? tabs[0] ?? null
})

const focusedPageState = computed(() => {
  const states = pageStates.value ?? []
  for (let i = states.length - 1; i >= 0; i -= 1) {
    const state = states[i]
    if (String(state?.element_level ?? '').toUpperCase() !== 'PAGE') continue
    if (state?.is_focused !== true) continue
    if (state?.element_id == null) continue
    return {
      id: normalizePageTabId(state.element_id),
      signature: `${i}:${normalizePageTabId(state.element_id)}`,
    }
  }
  return { id: '', signature: '' }
})

const focusedPageId = computed(() => focusedPageState.value.id)
const focusedPageSignature = computed(() => focusedPageState.value.signature)

const activeTabKey = computed(() => {
  const tab = activeVisibleTab.value
  const tid = normalizePageTabId(tab?.id ?? tab?._attributes?.id)
  return `${formId.value}:${tid}`
})

const panelsReady = (tab: any) => {
  if (!tab?.lazy) return true
  const sid = normalizePageTabId(tab?.id ?? tab?._attributes?.id)
  return Boolean(lazyPagesMounted.value[sid])
}

/** Активная страница (одиночная или выбранная вкладка) — для lazy load. */
const activePageForLazy = computed(() => {
  if (props.data?.length === 1) {
    const only = props.data[0]
    return only && isPageVisible(only) ? only : null
  }
  return activeVisibleTab.value
})

watch(formId, () => {
  lazyPagesMounted.value = {}
})

const tabHasOnClick = (tab: any) => Boolean(buildPageOnClickScriptData(tab)?._text?.trim())

const concealTabBodyIfSwitching = (tab: any, switching: boolean) => {
  if (!switching) return
  tabBodyRevealed.value = !tabHasOnClick(tab)
}

const runTabOnClickAndReveal = async (tab: any) => {
  try {
    await runTabOnClick(tab)
  } finally {
    tabBodyRevealed.value = true
  }
}

const replayTabBodyVisibility = async () => {
  await nextTick()
  const slot = pagesDataSlotForVisibility.value
  if (!slot) return
  formProvider?.replayPageVisibilityStatesForSlot?.(slot)
}

const selectTab = async (tab: any, idx: number) => {
  concealTabBodyIfSwitching(tab, currentTab.value !== idx)
  currentTab.value = idx
  await nextTick()
  await runTabOnClickAndReveal(tab)
}

watch(activeTabKey, () => {
  void replayTabBodyVisibility()
})

watch(
  visibleTabs,
  (tabs) => {
    if (currentTab.value >= tabs.length) {
      currentTab.value = Math.max(0, tabs.length - 1)
    }
  },
  { deep: true },
)

watch(
  [focusedPageSignature, visibleTabIdsKey],
  () => {
    const nextFocusedPageId = focusedPageId.value
    const visibleIds = visibleTabIdsKey.value ? visibleTabIdsKey.value.split('|') : []
    if (!nextFocusedPageId || visibleIds.length === 0) return
    const nextIdx = visibleIds.findIndex((id) => id === nextFocusedPageId)
    if (nextIdx >= 0 && currentTab.value !== nextIdx) {
      concealTabBodyIfSwitching(visibleTabs.value[nextIdx], true)
      currentTab.value = nextIdx
    }
  },
  { immediate: true },
)

watch(
  () => ({ tab: activePageForLazy.value, fid: formId.value }),
  async ({ tab, fid }) => {
    if (!tab || !fid || !tab.lazy) return
    const sid = String(tab.id)
    if (lazyPagesMounted.value[sid]) return

    lazyPagesMounted.value = { ...lazyPagesMounted.value, [sid]: true }

    await nextTick()

    const pid = parseInt(sid, 10)
    if (!Number.isFinite(pid) || !formProvider?.reloadLazyPage) return

    try {
      await formProvider.reloadLazyPage(pid, fid)
    } catch {
      /* reloadLazyPage уже логирует в GlobalProvider */
    }
  },
  { immediate: true },
)

const hasError = (tab: unknown) => {
  if (!Object.keys(pageErrors.value).length) return false
  return (
    !!(tab as { id?: string | number })?.id && pageErrors.value[(tab as { id: string | number }).id]
  )
}

const runTabOnClick = async (tab: any) => {
  const scriptData = buildPageOnClickScriptData(tab)
  const script = scriptData?._text?.trim() ?? ''
  if (!script) return

  for (let attempt = 0; attempt < 25; attempt++) {
    await nextTick()
    const snapshot = mergePageDataFieldSnapshot(pageData.value)
    const hasFields = Object.keys(snapshot).length > 0
    const hasSelectableTypes = Object.prototype.hasOwnProperty.call(snapshot, 'selectableTypeIds')
    if (hasFields && (hasSelectableTypes || !script.includes('selectableTypeIds'))) {
      break
    }
    if (attempt < 24) {
      await new Promise((resolve) => setTimeout(resolve, 16))
    }
  }

  const fid = String(formId.value || '').trim()
  const pageId = parseInt(String(tab?.id ?? tab?._attributes?.id ?? ''), 10)
  if (!fid || !Number.isFinite(pageId) || !formProvider?.runAction) return
  await formProvider.runAction({
    level: 'page',
    eventName: 'onClick',
    formId: fid,
    pageId: String(pageId),
    data: {
      ...tab,
      id: tab?.id ?? pageId,
      onClick: scriptData,
      _attributes: {
        ...(tab?._attributes || {}),
        id: String(tab?.id ?? pageId),
      },
    },
  })
}

watch(
  () => ({
    fid: formId.value,
    focusSig: focusedPageSignature.value,
    tabsKey: visibleTabIdsKey.value,
  }),
  async ({ fid, focusSig }) => {
    const formKey = String(fid || '').trim()
    if (!formKey || !focusSig) return

    const focusedId = focusedPageId.value
    if (!focusedId) return

    const tab = visibleTabs.value.find(
      (t: any) => normalizePageTabId(t?.id ?? t?._attributes?.id) === focusedId,
    )
    if (!tab) return

    const dedupeKey = `${formKey}:${focusSig}`
    if (lastFocusedTabOnClickSig.value === dedupeKey) return
    lastFocusedTabOnClickSig.value = dedupeKey

    await runTabOnClickAndReveal(tab)
  },
  { immediate: true },
)
</script>

<style scoped lang="scss">
.tabs-container {
  width: 100%;
  flex: 0 0 auto;
  margin-bottom: 0.5rem;

  /* Page owner (document, form grabVertical=false): leftover main, one inner bar. */
  &.tabs-container--page-scroll:not(.form-section--header):not(.form-section--footer) {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  /* Fill-table leftover (sized dialog and document --fill). */
  &:not(.form-section--header):not(.form-section--footer):has(.empty-tabs):has(.modern-table--fill),
  &:not(.form-section--header):not(.form-section--footer):has(.empty-tabs):has(.modern-tree-table--fill),
  &:not(.form-section--header):not(.form-section--footer):has(.tabs):has(.modern-table--fill),
  &:not(.form-section--header):not(.form-section--footer):has(.tabs):has(.modern-tree-table--fill) {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
}

.form-section--header,
.form-section--footer {
  flex: 0 0 auto;
  min-height: auto;
  overflow: visible;
}

.empty-tabs {
  width: 100%;
}

.tabs-container--page-scroll:not(.form-section--header):not(.form-section--footer) .empty-tabs {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  scrollbar-gutter: stable;
}

.tabs {
  background: #ffffff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.tabs-container--clip-inner .tabs {
  height: auto;
  overflow: visible;
  flex: 0 0 auto;
}

.tab-empty-hidden {
  min-height: 0;
}

.tab-headers {
  display: flex;
  background: #f8f9fa;
  border-bottom: 2px solid #e9ecef;
  overflow-x: auto;
  overflow-y: hidden;

  /* Smooth scrollbar */
  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
}

.tab-header {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.875rem 1.5rem;
  background: transparent;
  border: none;
  color: #6c757d;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;

  &:hover {
    color: var(--primary-color, #1e3a6e);
    background: color-mix(in srgb, var(--primary-color, #1e3a6e) 5%, transparent);
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--primary-color, #1e3a6e) 35%, white);
  }

  &.active {
    color: var(--primary-color, #1e3a6e);
    border-bottom-color: var(--primary-color, #1e3a6e);
    font-weight: 600;

    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      right: 0;
      height: 2px;
      background: var(--primary-color, #1e3a6e);
      box-shadow: none;
    }
  }
  &.errors {
    color: #e24c4c;
    border-bottom-color: #e24c4c;
    font-weight: 600;
  }
}

.tab-header-icon {
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
  object-fit: contain;
}

.tab-header-icon-prime {
  font-size: 14px;
  line-height: 1;
}

.tab-content {
  position: relative;
  display: grid;
  grid-template: minmax(8rem, 1fr) / 1fr;
  background: #ffffff;
  padding: 1.25rem;
  flex: 1;
  overflow: visible;
}

.tabs-container--page-scroll .tab-content {
  overflow: auto;
  scrollbar-gutter: stable;
}

.tabs-container--clip-inner .tab-content {
  overflow: visible;
}

.tabs-container--clip-inner .tab-panel {
  height: auto;
}

.tabs-container:has(.modern-table--fill) .tab-content,
.tabs-container:has(.modern-tree-table--fill) .tab-content {
  overflow: hidden;
  scrollbar-gutter: auto;
}

.tab-panel,
.tab-panel-pending {
  grid-area: 1 / 1;
}

.tab-panel-pending {
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 8rem;
  pointer-events: none;
}

.tab-panel-pending__spinner {
  box-sizing: border-box;
  width: 2rem;
  height: 2rem;
  border: 3px solid #dee2e6;
  border-top-color: var(--primary-color, #1e3a6e);
  border-radius: 50%;
  animation: tab-panel-spin 0.7s linear infinite;
}

.tab-panel {
  animation: fadeIn 0.2s ease-in;
  height: 100%;

  .container-fluid {
    height: 100%;
  }

  &--pending {
    visibility: hidden;
    animation: none;
  }
}

.page-actions-top {
  margin-bottom: 1rem;
  padding: 0.5rem 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes tab-panel-spin {
  to {
    transform: rotate(360deg);
  }
}

</style>
